import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Mail, Calendar, Shield, ArrowLeft, Settings, ChevronDown, ChevronUp, X, Save, Ban, CheckCircle } from 'lucide-react';
import NavigationBar from '../components/NavigationBar';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';

interface Professional {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  canPerformServices?: boolean;
  canAccessCustomers?: boolean;
  canViewFullPhoneNumber?: boolean;
  joinedAt: string;
  role?: string;
  roleIds?: string[];
}

interface UserRole {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  permissions: string[];
}

interface Service {
  id: string;
  name: string;
  description?: string;
  durationMinutes: number;
  price: number;
  isActive: boolean;
  category?: {
    id: string;
    name: string;
  };
}

interface ServiceCategoryGroup {
  id: string;
  name: string;
  services: Service[];
}

const UNCATEGORIZED_ID = '__uncategorized__';

const groupServicesByCategory = (serviceList: Service[]): ServiceCategoryGroup[] => {
  const groups = new Map<string, ServiceCategoryGroup>();

  for (const service of serviceList) {
    const id = service.category?.id || UNCATEGORIZED_ID;
    const name = service.category?.name || 'Uncategorized';
    const existing = groups.get(id);
    if (existing) {
      existing.services.push(service);
    } else {
      groups.set(id, { id, name, services: [service] });
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    if (a.id === UNCATEGORIZED_ID) return 1;
    if (b.id === UNCATEGORIZED_ID) return -1;
    return a.name.localeCompare(b.name);
  });
};

const EditPersonnelPage: React.FC = () => {
  const navigate = useNavigate();
  const { personnelId } = useParams<{ personnelId: string }>();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [personnel, setPersonnel] = useState<Professional | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [professionalServices, setProfessionalServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [canPerformServices, setCanPerformServices] = useState<boolean>(false);
  const [canAccessCustomers, setCanAccessCustomers] = useState<boolean>(true);
  const [customersAccessSaving, setCustomersAccessSaving] = useState(false);
  const [canViewFullPhoneNumber, setCanViewFullPhoneNumber] = useState<boolean>(true);
  const [phoneNumberViewSaving, setPhoneNumberViewSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const hasInitializedCategoryExpand = useRef(false);

  const parseCanAccessCustomersFromApi = (value: unknown): boolean => {
    if (value === false) return false;
    if (value === true) return true;
    return true;
  };

  const parseCanViewFullPhoneNumberFromApi = (value: unknown): boolean => {
    if (value === false) return false;
    if (value === true) return true;
    return true;
  };

  // Load services data
  const loadServicesData = async () => {
    if (!currentBusiness?.id || !personnelId) return;
    
    try {
      setServicesLoading(true);
      
      // Fetch all business services
      const servicesData = await apiService.getAllServices(currentBusiness.id);
      setServices(servicesData.filter((service: Service) => service.isActive));
      
      // Fetch professional's assigned services
      const professionalServicesData = await apiService.getProfessionalServices(currentBusiness.id, personnelId);
      setProfessionalServices(professionalServicesData);
      
    } catch (err: any) {
      console.error('Error loading services data:', err);
      toast.error('Failed to load services data');
    } finally {
      setServicesLoading(false);
    }
  };

  // Fetch personnel data and roles
  useEffect(() => {
    const fetchData = async () => {
      if (!personnelId || !currentBusiness?.id) {
        setError('Personnel ID and business are required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const personnelData = await apiService.getBusinessProfessionalsDetailed(currentBusiness.id);
        const currentPersonnel = personnelData.find((p: any) => p.id === personnelId);

        if (!currentPersonnel) {
          setError('Personnel not found');
          setLoading(false);
          return;
        }

        setPersonnel(currentPersonnel);
        setSelectedRoleIds(currentPersonnel.roleIds || []);
        setCanPerformServices(currentPersonnel.canPerformServices || false);
        setCanAccessCustomers(parseCanAccessCustomersFromApi(currentPersonnel.canAccessCustomers));
        setCanViewFullPhoneNumber(parseCanViewFullPhoneNumberFromApi(currentPersonnel.canViewFullPhoneNumber));

        const rolesData = await apiService.getBusinessRoles(currentBusiness.id);
        setRoles(
          rolesData.map((role: any) => ({
            id: role.id,
            name: role.name,
            description: role.description,
            isActive: role.is_active,
            permissions: role.permissions?.map((p: any) => p.name) || [],
          }))
        );

        if (currentPersonnel.canPerformServices) {
          await loadServicesData();
        }
      } catch (err: any) {
        console.error('Error fetching personnel data:', err);
        setError(err.response?.data?.message || 'Failed to load personnel data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [personnelId, currentBusiness?.id]);

  // Handle service selection/deselection
  const handleToggleServiceSelection = async (serviceId: string) => {
    if (!currentBusiness?.id || !personnelId) return;
    
    const isCurrentlySelected = professionalServices.some(ps => ps.id === serviceId);
    
    try {
      if (isCurrentlySelected) {
        // Remove service from professional's capabilities
        await apiService.unassignServiceFromProfessional(currentBusiness.id, personnelId, serviceId);
        toast.success('Service removed from personnel capabilities');
      } else {
        // Add service to professional's capabilities
        await apiService.assignServiceToProfessional(currentBusiness.id, personnelId, serviceId);
        toast.success('Service added to personnel capabilities');
      }
      
      // Reload professional services to update the UI
      await loadServicesData();
    } catch (error) {
      console.error('Error toggling service selection:', error);
      toast.error('Failed to update service selection');
    }
  };

  const handleRoleToggle = (roleId: string) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role?.isActive) return;

    setSelectedRoleIds(prev => {
      if (prev.includes(roleId)) {
        // If clicking on the currently selected role, don't allow deselection
        // (we need exactly 1 role selected)
        return prev;
      } else {
        // Replace the current selection with the new role (ensuring exactly 1 role)
        return [roleId];
      }
    });
  };

  const isPersonnelBusinessOwner = personnel?.role === 'Business Owner';

  const handleCustomersAccessToggle = async () => {
    if (!personnel || !currentBusiness?.id || isPersonnelBusinessOwner) return;

    const newValue = !canAccessCustomers;
    setCustomersAccessSaving(true);
    try {
      const result = await apiService.updateUserCustomersAccess(personnel.id, {
        businessId: currentBusiness.id,
        canAccessCustomers: newValue,
      });
      const saved = parseCanAccessCustomersFromApi(result?.canAccessCustomers ?? newValue);
      setCanAccessCustomers(saved);
      setPersonnel((prev) => (prev ? { ...prev, canAccessCustomers: saved } : prev));

      const refreshed = await apiService.getBusinessProfessionalsDetailed(currentBusiness.id);
      const match = refreshed.find((p: any) => p.id === personnel.id);
      if (match) {
        const persisted = parseCanAccessCustomersFromApi(match.canAccessCustomers);
        setCanAccessCustomers(persisted);
        setPersonnel((prev) => (prev ? { ...prev, canAccessCustomers: persisted } : prev));
      }

      toast.success(`Customers page access ${saved ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      console.error('Error updating customers access:', err);
      toast.error(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to update customers access');
    } finally {
      setCustomersAccessSaving(false);
    }
  };

  const handlePhoneNumberViewToggle = async () => {
    if (!personnel || !currentBusiness?.id || isPersonnelBusinessOwner) return;

    const newValue = !canViewFullPhoneNumber;
    setPhoneNumberViewSaving(true);
    try {
      const result = await apiService.updateUserPhoneNumberViewAccess(personnel.id, {
        businessId: currentBusiness.id,
        canViewFullPhoneNumber: newValue,
      });
      const saved = parseCanViewFullPhoneNumberFromApi(result?.canViewFullPhoneNumber ?? newValue);
      setCanViewFullPhoneNumber(saved);
      setPersonnel((prev) => (prev ? { ...prev, canViewFullPhoneNumber: saved } : prev));

      const refreshed = await apiService.getBusinessProfessionalsDetailed(currentBusiness.id);
      const match = refreshed.find((p: any) => p.id === personnel.id);
      if (match) {
        const persisted = parseCanViewFullPhoneNumberFromApi(match.canViewFullPhoneNumber);
        setCanViewFullPhoneNumber(persisted);
        setPersonnel((prev) => (prev ? { ...prev, canViewFullPhoneNumber: persisted } : prev));
      }

      toast.success(`Full phone number view ${saved ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      console.error('Error updating phone number view access:', err);
      toast.error(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to update phone number view access');
    } finally {
      setPhoneNumberViewSaving(false);
    }
  };

  const handleServicePermissionToggle = async () => {
    if (!personnel || !currentBusiness?.id) return;
    
    try {
      const newValue = !canPerformServices;
      await apiService.updateUserServicePermissions(personnel.id, {
        canPerformServices: newValue
      });
      
      setCanPerformServices(newValue);
      
      // If enabling service permissions, load services data
      if (newValue) {
        await loadServicesData();
      } else {
        // If disabling, clear services data
        setServices([]);
        setProfessionalServices([]);
        setExpandedCategories({});
        hasInitializedCategoryExpand.current = false;
      }
      
      toast.success(`Service permissions ${newValue ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      console.error('Error updating service permissions:', err);
      toast.error(err.response?.data?.message || 'Failed to update service permissions');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!personnel || !currentBusiness?.id) {
      toast.error('Personnel and business information required');
      return;
    }

    if (selectedRoleIds.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Update roles
      await apiService.updatePersonnelRoles(currentBusiness.id, personnel.id, selectedRoleIds);
      
      toast.success('Personnel updated successfully');
      navigate('/dashboard/business?section=professionals');
      
    } catch (err: any) {
      console.error('Error updating personnel:', err);
      toast.error(err.response?.data?.message || 'Failed to update personnel');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/business?section=professionals');
  };

  const getInitials = (name: string): string => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleSectionChange = (section: string) => {
    navigate(`/dashboard/business?section=${section}`);
  };

  const serviceCategoryGroups = groupServicesByCategory(services);

  useEffect(() => {
    if (services.length === 0) {
      hasInitializedCategoryExpand.current = false;
      setExpandedCategories({});
      return;
    }

    if (!hasInitializedCategoryExpand.current) {
      const next: Record<string, boolean> = {};
      for (const group of groupServicesByCategory(services)) {
        next[group.id] = true;
      }
      setExpandedCategories(next);
      hasInitializedCategoryExpand.current = true;
      return;
    }

    setExpandedCategories((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const group of groupServicesByCategory(services)) {
        if (!(group.id in next)) {
          next[group.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [services]);

  const toggleCategoryExpanded = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const expandAllCategories = () => {
    const next: Record<string, boolean> = {};
    for (const group of serviceCategoryGroups) {
      next[group.id] = true;
    }
    setExpandedCategories(next);
  };

  const collapseAllCategories = () => {
    const next: Record<string, boolean> = {};
    for (const group of serviceCategoryGroups) {
      next[group.id] = false;
    }
    setExpandedCategories(next);
  };

  const allCategoriesExpanded =
    serviceCategoryGroups.length > 0 &&
    serviceCategoryGroups.every((group) => expandedCategories[group.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading personnel data...</p>
        </div>
      </div>
    );
  }

  if (error || !personnel) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationBar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Personnel</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleCancel}
              className="aacore-btn2-primary"
            >
              Back to Personnel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar />
      
      <div className="lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="professionals"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={toggleSidebar}
            onSectionChange={handleSectionChange}
          />
        )}
        
        {/* Main Content */}
        <div className="flex flex-col min-h-screen">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Edit Personnel</h1>
              <div className="w-10"></div> {/* Spacer for centering */}
            </div>
          </div>
          
          <div className="flex-1 p-4 md:p-8">
            {/* Breadcrumb Navigation */}
            <div className="mb-4">
              <button
                onClick={() => navigate('/dashboard/business?section=professionals')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Personnel</span>
              </button>
            </div>

            {/* Header */}
            <div className="mb-6">
              <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Edit Personnel</h2>
              <p className="text-gray-600 mt-1">Update personnel information and role assignments</p>
            </div>

            {/* Content */}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personnel Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Personnel Information</h2>
                
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold text-lg">
                      {getInitials(personnel.name)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{personnel.name}</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        <span>{personnel.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>Joined {formatDate(personnel.joinedAt)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          personnel.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {personnel.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Service Permission Status */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                      <Settings className="h-5 w-5 text-gray-600 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Service Permissions</h3>
                        <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                          canPerformServices
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {canPerformServices ? 'Can Perform Services' : 'Cannot Perform Services'}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleServicePermissionToggle}
                      className={canPerformServices ? 'aacore-btn2-danger' : 'aacore-btn2-primary'}
                    >
                      {canPerformServices ? (
                        <>
                          <Ban className="w-4 h-4" />
                          <span>Disable Services</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span>Enable Services</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Role Assignment */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Role Assignment</h2>
                <p className="text-gray-600 mb-6">Select exactly one role to assign to this personnel. Click on a role card to select it.</p>
                
                {roles.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-4">👥</div>
                    <p className="text-gray-600">No roles available</p>
                    <p className="text-sm text-gray-500">Create roles first to assign them to personnel</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {roles.map((role) => {
                      const isSelected = selectedRoleIds.includes(role.id);
                      const isDisabled = !role.isActive;

                      return (
                      <div
                        key={role.id}
                        onClick={() => handleRoleToggle(role.id)}
                        aria-disabled={isDisabled}
                        className={`p-4 border-2 rounded-lg transition-all duration-200 ${
                          isDisabled
                            ? 'opacity-40 grayscale cursor-not-allowed border-gray-200 bg-gray-50'
                            : `cursor-pointer ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                              }`
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-gray-900">{role.name}</h3>
                          {isSelected && (
                            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{role.description}</p>
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            role.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {role.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {role.permissions.length} permissions
                          </span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Others */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Others</h2>
                <div className="flex items-center justify-between md:justify-start gap-4 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={handleCustomersAccessToggle}
                    disabled={customersAccessSaving || isPersonnelBusinessOwner}
                    className={`order-2 md:order-1 relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${
                      canAccessCustomers ? 'bg-[#0B4778]' : 'bg-gray-200'
                    } ${customersAccessSaving || isPersonnelBusinessOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-checked={canAccessCustomers}
                    role="switch"
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                        canAccessCustomers ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <div className="order-1 md:order-2 flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900">Customers page access</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Allow this person to view the Customers section in the business dashboard.
                    </p>
                    {isPersonnelBusinessOwner && (
                      <p className="text-xs text-gray-500 mt-2">Business owners always have access.</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between md:justify-start gap-4 border-t border-gray-200 pt-4 mt-4">
                  <button
                    type="button"
                    onClick={handlePhoneNumberViewToggle}
                    disabled={phoneNumberViewSaving || isPersonnelBusinessOwner}
                    className={`order-2 md:order-1 relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${
                      canViewFullPhoneNumber ? 'bg-[#0B4778]' : 'bg-gray-200'
                    } ${phoneNumberViewSaving || isPersonnelBusinessOwner ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-checked={canViewFullPhoneNumber}
                    role="switch"
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                        canViewFullPhoneNumber ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                  <div className="order-1 md:order-2 flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900">View full phone number</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Allow this person to view the complete customer phone number instead of only the last 4 digits.
                    </p>
                    {isPersonnelBusinessOwner && (
                      <p className="text-xs text-gray-500 mt-2">Business owners can always view full phone numbers.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Services Assignment - Only show if canPerformServices is true */}
              {canPerformServices && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900">Services Assignment</h2>
                      <p className="text-gray-600 mt-1 text-sm sm:text-base">
                        Select the services this personnel can perform for customers. Click on a service card to select or deselect it.
                      </p>
                    </div>
                    {!servicesLoading && serviceCategoryGroups.length > 1 && (
                      <button
                        type="button"
                        onClick={allCategoriesExpanded ? collapseAllCategories : expandAllCategories}
                        className="self-start shrink-0 text-sm font-medium text-[#0B4778] hover:text-[#083558] transition-colors py-1"
                      >
                        {allCategoriesExpanded ? 'Collapse all' : 'Expand all'}
                      </button>
                    )}
                  </div>
                  
                  {servicesLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading services...</p>
                    </div>
                  ) : services.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-4xl mb-4">🛠️</div>
                      <p className="text-gray-600">No services available</p>
                      <p className="text-sm text-gray-500">Create services first to assign them to personnel</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {serviceCategoryGroups.map((group) => {
                        const isExpanded = !!expandedCategories[group.id];
                        const selectedCount = group.services.filter((service) =>
                          professionalServices.some((ps) => ps.id === service.id)
                        ).length;

                        return (
                          <div
                            key={group.id}
                            className="border border-gray-200 rounded-lg overflow-hidden"
                          >
                            <button
                              type="button"
                              onClick={() => toggleCategoryExpanded(group.id)}
                              aria-expanded={isExpanded}
                              className="w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left touch-manipulation"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-gray-900 truncate">
                                    {group.name}
                                  </h3>
                                  <span className="text-xs font-medium text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full shrink-0">
                                    {group.services.length} {group.services.length === 1 ? 'service' : 'services'}
                                  </span>
                                  {selectedCount > 0 && (
                                    <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full shrink-0">
                                      {selectedCount} selected
                                    </span>
                                  )}
                                </div>
                              </div>
                              {isExpanded ? (
                                <ChevronUp className="h-5 w-5 text-gray-500 shrink-0" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-gray-500 shrink-0" />
                              )}
                            </button>

                            {isExpanded && (
                              <div className="p-3 sm:p-4 border-t border-gray-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                  {group.services.map((service) => {
                                    const isSelected = professionalServices.some(ps => ps.id === service.id);
                                    return (
                                      <div 
                                        key={service.id} 
                                        className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                                          isSelected 
                                            ? 'border-blue-500 bg-blue-50 shadow-md' 
                                            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                                        }`}
                                        onClick={() => handleToggleServiceSelection(service.id)}
                                      >
                                        <div className="flex items-start justify-between mb-3">
                                          <div className="flex items-center space-x-2 min-w-0">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                              isSelected 
                                                ? 'bg-blue-600 text-white' 
                                                : 'bg-gray-200 text-gray-600'
                                            }`}>
                                              {isSelected ? '✓' : '+'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <h3 className="font-medium text-gray-900">{service.name}</h3>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        <div className="space-y-2">
                                          {service.description && (
                                            <p className="text-sm text-gray-600 line-clamp-2">{service.description}</p>
                                          )}
                                          
                                          <div className="flex items-center justify-between gap-2 text-sm">
                                            <div className="flex items-center space-x-4 text-gray-600">
                                              <span>{service.durationMinutes} min</span>
                                              <span>${service.price}</span>
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium shrink-0 ${
                                              isSelected ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                              {isSelected ? 'Selected' : 'Not Selected'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="aacore-btn2-outline"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      <span>Update Personnel</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPersonnelPage; 